import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { StatusCard } from "../ui/StatusCard";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, Search, Calendar, Eye, Download } from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import Pagination from "../ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { format } from "date-fns";
import OrderDetailsModal from "./OrderDetailsModal";
import { useAuth } from "../../lib/authContext";

interface PendingOrder {
  tracking_id: string;
  customer_name: string;
  customer_phone: string;
  created_at: string;
  total: number;
  status: string;
  payment_status: string;
  payment_method: string;
  expected_delivery_date: string;
  prescription_required: boolean;
}

// Only these statuses are truly "pending" — not yet fulfilled
const PENDING_STATUSES = [
  "PENDING",
  "VALIDATING_PRESCRIPTION",
  "PRESCRIPTION_REJECTED",
];

const PendingOrders = () => {
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState("20");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [groupedOrders, setGroupedOrders] = useState<
    { status: string; count: number }[]
  >([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const { excelPermissions } = useAuth();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit,
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        // If user selected a specific status filter, use it; otherwise filter to pending statuses only
        ...(statusFilter
          ? { status: statusFilter }
          : { statuses: PENDING_STATUSES.join(",") }),
      });

      const response = await api.getB2COrders(params);

      const responseData = response.data;

      if (response.status === "success") {
        // Handle different possible response structures
        const ordersData = responseData.orders || responseData || [];
        const paginationData =
          responseData.data?.pagination || responseData.pagination || {};

        setOrders(ordersData);
        setTotalPages(paginationData.totalPages || 1);
      } else if (responseData.data) {
        // Handle case where success flag might not be present
        const ordersData = responseData.orders || responseData || [];
        const paginationData = responseData.pagination || {};

        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setTotalPages(paginationData.totalPages || 1);
      } else {
        toast.error("Failed to fetch orders");
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Error fetching orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupedOrders = async () => {
    try {
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        statuses: PENDING_STATUSES.join(","),
      });

      const response = await api.getGroupedB2COrders(params);

      if (response.status === "success") {
        // Only show pending status groups
        const pendingGroups = response.data.filter((g: { status: string }) =>
          PENDING_STATUSES.includes(g.status),
        );
        setGroupedOrders(pendingGroups);
        const total = response.data.reduce(
          (acc: number, curr: { count: number }) => acc + Number(curr.count),
          0,
        );
        setTotalOrders(total);
      }
    } catch (error) {
      console.error("Error fetching grouped orders:", error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, limit, startDate, endDate, statusFilter]);

  useEffect(() => {
    fetchGroupedOrders();
  }, []);

  const handleSearch = () => {
    setPage(1);
    setStatusFilter("");
    fetchOrders();
    fetchGroupedOrders();
  };

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(statusFilter && { status: statusFilter }),
      });

      const response = await api.getB2COrdersExcel(params);

      if (response.success && response.data) {
        // Convert base64 to blob
        const byteCharacters = atob(response.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = response.filename || "b2c_orders.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Excel file downloaded successfully");
      } else {
        toast.error(response.error || "Failed to download Excel file");
      }
    } catch (error) {
      console.error("Error downloading excel:", error);
      toast.error("Error downloading Excel file");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm");
    } catch {
      return dateString;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "secondary";
      case "processing":
        return "default";
      case "completed":
        return "default";
      case "cancelled":
        return "destructive";
      default:
        return "secondary";
    }
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>B2C Pending Orders</CardTitle>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <StatusCard
              status="Total Orders"
              count={totalOrders}
              isSelected={false}
              onClick={() => {}}
            />
            {groupedOrders.map((order: { status: string; count: number }) => (
              <StatusCard
                key={order.status}
                status={order.status}
                count={order.count}
                isSelected={statusFilter === order.status}
                onClick={() => {
                  if (statusFilter === order.status) {
                    setStatusFilter("");
                  } else {
                    setStatusFilter(order.status);
                  }
                  setPage(1);
                }}
              />
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search orders..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyPress={e => e.key === "Enter" && handleSearch()}
                  className="pl-10"
                />
              </div>

              {/* Start Date */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* End Date */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Additional Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Limit */}
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="20">20 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>

              {/* Search and Download Buttons */}
              <div className="flex justify-end gap-2">
                {excelPermissions.canDownloadExcel && (
                  <Button
                    onClick={handleDownloadExcel}
                    disabled={loading}
                    variant="outline"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Excel
                  </Button>
                )}
                <Button onClick={handleSearch} disabled={loading}>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          {loading ? (
            <div className="flex justify-center items-center h-64 mt-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto mt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Order Date</TableHead>
                      <TableHead>Total Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Payment Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map(order => (
                      <TableRow key={order.tracking_id}>
                        <TableCell className="font-medium">
                          {order.tracking_id}
                        </TableCell>
                        <TableCell>{order.customer_name}</TableCell>
                        <TableCell>{order.customer_phone}</TableCell>
                        <TableCell>{formatDate(order.created_at)}</TableCell>
                        <TableCell>
                          ₹{(parseFloat(String(order.total)) || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={getStatusBadgeVariant(order.status) as any}
                          >
                            {order.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              order.payment_status === "paid"
                                ? "default" // Changed from "success" to "default" to match allowed variants
                                : "secondary"
                            }
                          >
                            {order.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewOrder(order.tracking_id)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {orders.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No orders found
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-center mt-4">
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Order Details Modal */}
      {selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default PendingOrders;
